import React, { useState, useMemo } from 'react';
import { 
  BookOpen, 
  Search, 
  Calendar,
  Filter,
  ArrowRightLeft
} from 'lucide-react';
import { JournalEntry, AccountingAccount } from '../../types';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { safeFormat } from '../../utils/dateUtils';

interface LedgerViewProps {
  entries: JournalEntry[];
  accounts: AccountingAccount[];
}

export const LedgerView: React.FC<LedgerViewProps> = ({ entries, accounts }) => {
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const selectableAccounts = useMemo(() => 
    accounts.filter(a => a.isSelectable).sort((a, b) => a.code.localeCompare(b.code)),
  [accounts]);

  const ledgerData = useMemo(() => {
    if (!selectedAccountId) return [];

    let runningBalance = 0;
    const account = accounts.find(a => a.id === selectedAccountId);
    if (account) runningBalance = account.initialBalance || 0;

    // Filter lines for this account
    const lines = entries.flatMap(e => 
      e.lines
        .filter(l => l.accountId === selectedAccountId)
        .map(l => ({ 
          ...l, 
          date: e.date, 
          entryDescription: e.description, 
          entryId: e.id, 
          type: e.type,
          documentLocatorCode: e.documentLocatorCode 
        }))
    ).sort((a, b) => a.date.localeCompare(b.date));

    return lines.map(line => {
      // Calculate balance based on account category
      // Assets and Expenses: Balance increases with Debit, decreases with Credit
      // Liabilities, Equity, Incomes: Balance increases with Credit, decreases with Debit
      const isNaturalDebit = ['ACTIVO', 'EGRESO'].includes(account?.category || '');
      
      if (isNaturalDebit) {
        runningBalance += (line.debit - line.credit);
      } else {
        runningBalance += (line.credit - line.debit);
      }

      return {
        ...line,
        runningBalance
      };
    });
  }, [selectedAccountId, entries, accounts]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl">
      <div className="p-6 border-b border-zinc-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex-1 max-w-xl">
          <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2 block">Seleccionar Cuenta Contable</label>
          <select 
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
          >
            <option value="">-- Elige una cuenta --</option>
            {selectableAccounts.map(acc => (
              <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
            ))}
          </select>
        </div>
      </div>

      {!selectedAccountId ? (
        <div className="flex flex-col items-center justify-center py-32 text-zinc-500 gap-4">
          <BookOpen size={48} className="opacity-20" />
          <p>Selecciona una cuenta para ver su Libro Mayor.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-950/50 text-[11px] font-bold text-zinc-500 uppercase tracking-widest border-b border-zinc-800">
                <th className="px-6 py-4 w-32">Fecha</th>
                <th className="px-6 py-4">Descripción del Asiento</th>
                <th className="px-6 py-4 text-right w-32">Debe</th>
                <th className="px-6 py-4 text-right w-32">Haber</th>
                <th className="px-6 py-4 text-right w-40">Saldo Acum.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800 font-mono text-xs">
              {ledgerData.map((item, idx) => (
                <tr key={idx} className="hover:bg-zinc-800/20 transition-all">
                  <td className="px-6 py-4 text-zinc-400">
                    {safeFormat(item.date, 'dd/MM/yyyy')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 shrink-0">
                           <span className="text-[9px] text-indigo-400 bg-indigo-500/10 px-1.5 py-0.5 rounded font-mono font-bold" title="ID de Asiento">
                             #{item.entryId.slice(-4).toUpperCase()}
                           </span>
                           {item.documentLocatorCode ? (
                             <span className="text-[9px] font-mono font-bold bg-white text-black px-1.5 py-0.5 rounded tracking-widest shadow-lg shadow-white/5" title="Código de Referencia del Documento">
                               REF: {item.documentLocatorCode}
                             </span>
                           ) : (
                             <span className="text-[8px] font-bold text-zinc-600 bg-zinc-800/50 px-1 py-0.5 rounded uppercase tracking-tighter">
                               S/Ref
                             </span>
                           )}
                        </div>
                        <div className="text-white font-medium truncate">{item.entryDescription}</div>
                      </div>
                      <div className="text-[9px] text-zinc-600 uppercase font-bold tracking-widest flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-zinc-700 ml-[1px]"></span>
                        {item.type}
                      </div>
                    </div>
                  </td>
                  <td className={`px-6 py-4 text-right ${item.debit > 0 ? 'text-emerald-400' : 'text-zinc-700'}`}>
                    {item.debit > 0 ? formatCurrency(item.debit) : '-'}
                  </td>
                  <td className={`px-6 py-4 text-right ${item.credit > 0 ? 'text-red-400' : 'text-zinc-700'}`}>
                    {item.credit > 0 ? formatCurrency(item.credit) : '-'}
                  </td>
                  <td className={`px-6 py-4 text-right font-bold ${item.runningBalance >= 0 ? 'text-indigo-400' : 'text-red-400'}`}>
                    {formatCurrency(item.runningBalance)}
                  </td>
                </tr>
              ))}
              {ledgerData.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-20 text-center text-zinc-500">
                    No hay movimientos registrados para esta cuenta.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
